/* eslint-disable react/jsx-props-no-spreading */
import type { Seller } from 'payments-api/model/seller';
import {
  useGetProductsSuspense,
  useGetUsersSuspense,
  usePostProducts,
} from 'api/payments/generated/fragmentPaymentsAPI';
import {
  PaymentViewLayout,
  PaymentViewLoading,
} from 'components/Layout/PaymentViewLayout';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { capitalize } from 'lodash-es';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { ErrorText } from '@fragment/ui/src/components/ErrorText';
import { Modal } from '@fragment/ui/src/components/Modal/Modal';
import { ModalDropdown } from '@fragment/ui/src/components/Modal/ModalDropdown';
import { ModalField } from '@fragment/ui/src/components/Modal/ModalField';
import { ModalTextInput } from '@fragment/ui/src/components/Modal/ModalTextInput';
import type { ColumnDefinition } from '@fragment/ui/src/components/Table';
import { NavTableV2 } from '@fragment/ui/src/components/Table/NavTableV2';

type Props = {
  workspaceId: string;
};

type ProductRow = {
  id: string;
  code: string;
  description: string;
  seller: string;
};

type ProductFormData = {
  code: string;
  description: string;
  sellerType: 'platform' | 'user';
  role: string;
};

const emptyValue = {
  Code: 'No Products Yet',
  Description: 'N/A',
  Seller: 'N/A',
};

const schema: ColumnDefinition<ProductRow>[] = [
  {
    title: 'Code',
    key: 'code',
    colspan: 'col-span-2',
  },
  {
    title: 'Description',
    key: 'description',
    colspan: 'col-span-4',
  },
  {
    title: 'Seller',
    key: 'seller',
    justify: 'right',
    colspan: 'col-span-2',
  },
];

const ProductListPageContent = ({ workspaceId }: Props): JSX.Element => {
  useDocumentTitle('Products');

  const { data } = useGetProductsSuspense();
  // Note: Suspense handles loading state, error boundary handles errors
  const { data: usersData } = useGetUsersSuspense();
  const queryClient = useQueryClient();
  const createProductMutation = usePostProducts();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createError, setCreateError] = useState<string | undefined>();

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors, isValid },
  } = useForm<ProductFormData>({
    mode: 'onChange',
    defaultValues: {
      code: '',
      description: '',
      sellerType: 'platform' as const,
      role: '',
    },
  });

  const sellerType = watch('sellerType');

  // Extract products from the API response and transform to table rows
  const products: ProductRow[] = useMemo(
    () =>
      data.data.map((product) => ({
        id: product.code,
        code: product.code,
        description: product.description,
        seller: product.seller.soldByPlatform
          ? 'Platform'
          : capitalize((product.seller as { role: string }).role),
      })),
    [data]
  );

  // Extract unique roles from all users
  const userRoles = useMemo(() => {
    const users = usersData.data;
    const roles = users
      .map((user) => user.role)
      .filter((role): role is string => !!role);
    return Array.from(new Set(roles)).sort();
  }, [usersData]);

  const onSubmit = useCallback(
    async (formData: ProductFormData) => {
      setCreateError(undefined);
      try {
        const seller: Seller =
          formData.sellerType === 'platform'
            ? { soldByPlatform: true }
            : {
                soldByPlatform: false,
                role: formData.role,
              };

        await createProductMutation.mutateAsync({
          data: {
            code: formData.code,
            description: formData.description,
            seller,
          },
        });

        // Refetch the products list
        await queryClient.invalidateQueries({ queryKey: ['/products'] });

        // Close modal and reset form
        setShowCreateModal(false);
        reset();
      } catch (err) {
        setCreateError(
          err instanceof Error ? err.message : 'Failed to create product'
        );
      }
    },
    [createProductMutation, queryClient, reset]
  );

  const handleCloseModal = useCallback(() => {
    setShowCreateModal(false);
    reset();
    setCreateError(undefined);
  }, [reset]);

  return (
    <PaymentViewLayout
      title="Products"
      primaryAction={{
        title: 'Add',
        onClick: () => setShowCreateModal(true),
        'data-testid': 'create-product-button',
      }}
    >
      <div className="flex flex-col min-h-f1 w-full h-full pt-f4">
        <NavTableV2
          textSize="md"
          data={products}
          emptyValue={emptyValue}
          getPath={() => '#'}
          schema={schema}
          showHeader={false}
        />
        {showCreateModal && (
          <Modal
            onClose={handleCloseModal}
            header="Create Product"
            onSubmit={handleSubmit(onSubmit)}
            body={
              <div className="flex flex-col space-y-f2">
                <ModalField label={<label htmlFor="code">Code</label>}>
                  <ModalTextInput
                    id="code"
                    data-testid="product-code-input"
                    placeholder="product_123"
                    {...register('code', {
                      required: {
                        value: true,
                        message: 'Product code is required',
                      },
                    })}
                  />
                </ModalField>
                {errors.code && <ErrorText>{errors.code.message}</ErrorText>}
                <ModalField
                  label={<label htmlFor="description">Description</label>}
                >
                  <ModalTextInput
                    id="description"
                    data-testid="product-description-input"
                    placeholder="Product description"
                    {...register('description', {
                      required: {
                        value: true,
                        message: 'Product description is required',
                      },
                    })}
                  />
                </ModalField>
                {errors.description && (
                  <ErrorText>{errors.description.message}</ErrorText>
                )}
                <ModalField label={<label htmlFor="sellerType">Seller</label>}>
                  <Controller
                    control={control}
                    name="sellerType"
                    render={({ field }) => (
                      <ModalDropdown
                        data-testid="product-seller-type-dropdown"
                        value={field.value}
                        options={['platform', 'user']}
                        onChange={field.onChange}
                        placeholder="Select seller type"
                      />
                    )}
                  />
                </ModalField>
                {sellerType === 'user' && (
                  <ModalField label={<label htmlFor="role">User Role</label>}>
                    <Controller
                      control={control}
                      name="role"
                      rules={{
                        required: {
                          value: true,
                          message: 'User role is required',
                        },
                      }}
                      render={({ field }) => (
                        <ModalDropdown
                          data-testid="product-user-role-dropdown"
                          value={field.value}
                          options={userRoles}
                          onChange={field.onChange}
                          placeholder="Select user role"
                        />
                      )}
                    />
                    {errors.role && (
                      <ErrorText>{errors.role.message}</ErrorText>
                    )}
                  </ModalField>
                )}
              </div>
            }
            footer={
              <div className="flex flex-col gap-y-f2 w-full">
                {createError && <ErrorText>{createError}</ErrorText>}
                <div className="flex flex-row justify-between">
                  <FormButtons
                    type="secondary"
                    cancelButtonLabel="Cancel"
                    onCancel={handleCloseModal}
                    submitButtonLabel={
                      createProductMutation.isPending ? 'Creating...' : 'Create'
                    }
                    submitButtonTestId="product-submit-button"
                    disabled={createProductMutation.isPending || !isValid}
                  />
                </div>
              </div>
            }
          />
        )}
      </div>
    </PaymentViewLayout>
  );
};

export const ProductListPage = ({ workspaceId }: Props): JSX.Element => (
  <Suspense fallback={<PaymentViewLoading message="Loading products..." />}>
    <ProductListPageContent workspaceId={workspaceId} />
  </Suspense>
);
